// ListinoPDF.tsx

import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    Font
} from "@react-pdf/renderer";
import { Previsione } from "./previsioniSlice";

// Registra font per supporto caratteri italiani
Font.register({
    family: 'Roboto',
    fonts: [
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf', fontWeight: 'normal' },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 'bold' },
    ]
});

// Path del logo
import logoPath from "../../../../../static/images/logo/logo-Everest.png";

// Funzione per troncare il testo con ellipsis
const truncateText = (text: string | null | undefined, maxLength: number): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
};

// Stili
const styles = StyleSheet.create({
    page: {
        padding: 30,
        paddingBottom: 60,
        fontSize: 9,
        fontFamily: 'Roboto',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#E6D79C',
        paddingBottom: 15,
    },
    headerLeft: {
        flexDirection: 'column',
    },
    logo: {
        width: 120,
        height: 'auto',
        maxHeight: 50,
    },
    title: {
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 5,
        color: '#333',
    },
    subtitle: {
        fontSize: 10,
        color: '#666',
    },
    table: {
        width: '100%',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#E6D79C',
        color: '#fff',
        fontWeight: 'bold',
        borderBottomWidth: 1,
        borderBottomColor: '#000',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#ccc',
        minHeight: 20,
        alignItems: 'center',
    },
    tableRowAlternate: {
        backgroundColor: '#f5f5f5',
    },
    // Colonne con larghezze proporzionali (valori di default, sovrascritti dinamicamente)
    colUm: { width: '6%', padding: 4, textAlign: 'center' },
    colPrezzo: { width: '6%', padding: 4, textAlign: 'right' },
    // Stili testo header
    headerText: {
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 8,
    },
    cellText: {
        fontSize: 8,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 30,
        right: 30,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 8,
        color: '#666',
        borderTopWidth: 0.5,
        borderTopColor: '#ccc',
        paddingTop: 10,
    },
});

// Lunghezze massime caratteri per colonna (stimate in base alla larghezza)
const MAX_CHARS = {
    codice: 12,
    articolo: 18,
    descrizione: 28,
};

interface ListinoPDFProps {
    stagione?: string;
    mandatario: string;
    previsioni: Previsione[];
    mostraImpianto?: boolean;
}


const ListinoPDF: React.FC<ListinoPDFProps> = ({ stagione, mandatario, previsioni, mostraImpianto = false }) => {
    const dataGenerazione = new Date().toLocaleDateString('it-IT');

    // Larghezze colonne dinamiche in base a mostraImpianto
    const colCodice = { width: '10%', padding: 4 };
    const colArticolo = { width: '21%', padding: 4 };
    const colDescrizione = { width: '30%', padding: 4 };
    const colConfezionista = { width: mostraImpianto ? '14%' : '20%', padding: 4 };

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                {/* Header con Logo */}
                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        <Text style={styles.title}>Listino {stagione} - {mandatario}</Text>
                        <Text style={styles.subtitle}>Data: {dataGenerazione}</Text>
                    </View>
                    <Image style={styles.logo} src={logoPath} />
                </View>

                {/* Tabella */}
                <View style={styles.table}>
                    {/* Header tabella */}
                    <View fixed style={styles.tableHeader}>
                        <Text style={[colCodice, styles.headerText]}>Codice Ns Ricamo</Text>
                        <Text style={[colArticolo, styles.headerText]}>Articolo Cliente</Text>
                        <Text style={[colDescrizione, styles.headerText]}>Descrizione</Text>
                        <Text style={[colConfezionista, styles.headerText]}>Confezionista</Text>
                        <Text style={[styles.colUm, styles.headerText]}>UM</Text>
                        <Text style={[styles.colPrezzo, styles.headerText]}>PX Prod</Text>
                        <Text style={[styles.colPrezzo, styles.headerText]}>PX Camp</Text>
                        {mostraImpianto && (
                            <Text style={[styles.colPrezzo, styles.headerText]}>Impianto</Text>
                        )}
                    </View>

                    {/* Righe dati */}
                    {previsioni.map((p, index) => (
                        <View
                            key={p.id}
                            style={[
                                styles.tableRow,
                                index % 2 === 1 ? styles.tableRowAlternate : {}
                            ]}
                        >
                            <Text style={[colCodice, styles.cellText]}>{truncateText(p.codice_item, MAX_CHARS.codice)}</Text>
                            <Text style={[colArticolo, styles.cellText]}>{truncateText(p.articolo_cliente, MAX_CHARS.articolo)}</Text>
                            <Text style={[colDescrizione, styles.cellText]}>{p.descrizione_item || ''}</Text>
                            <Text style={[colConfezionista, styles.cellText]}>{p.confezionista_display || ''}</Text>
                            <Text style={[styles.colUm, styles.cellText]}>{p.unita_misura_display || ''}</Text>
                            <Text style={[styles.colPrezzo, styles.cellText]}>
                                {p.px_prod ? `€ ${new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(p.px_prod))}` : ''}
                            </Text>
                            <Text style={[styles.colPrezzo, styles.cellText]}>
                                {p.px_camp ? `€ ${new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(p.px_camp))}` : ''}
                            </Text>
                            {mostraImpianto && (
                                <Text style={[styles.colPrezzo, styles.cellText]}>
                                    {p.impianto ? `€ ${new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(p.impianto))}` : ''}
                                </Text>
                            )}
                        </View>
                    ))}
                </View>

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text>Listino {stagione} - {mandatario}</Text>
                    <Text>Everest s.r.l.</Text>
                    <Text>Data: {dataGenerazione}</Text>
                    <Text render={({ pageNumber, totalPages }) => `Pagina ${pageNumber} di ${totalPages}`} />
                </View>
            </Page>
        </Document>
    );
};

export default ListinoPDF;